/**
 * Utility functions for image format conversion
 * Converts various image formats to PNG format for Google Gemini compatibility
 * Gemini 2.5 Flash Image model requires PNG format for image generation
 */

import { logger } from "./logger";

/**
 * Supported image formats that can be converted to PNG
 */
export const SUPPORTED_IMAGE_FORMATS = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/heic",
  "image/heif",
] as const;

/**
 * Converts an image file to PNG format
 * Supports: JPEG, JPG, WEBP, HEIC, HEIF
 * @param file - The image file to convert
 * @returns Promise<File> - The converted PNG file
 */
export async function convertImageToPng(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    let objectUrl: string | null = null;

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      img.onload = () => {
        try {
          // Validate image dimensions (Gemini max: 3072x3072)
          const maxDimension = 3072;
          let width = img.width;
          let height = img.height;

          // Scale down if needed while preserving aspect ratio
          if (width > maxDimension || height > maxDimension) {
            const scale = Math.min(maxDimension / width, maxDimension / height);
            width = Math.floor(width * scale);
            height = Math.floor(height * scale);
            logger.info("Scaling image to fit Gemini requirements", {
              component: "ImageConverter",
              metadata: {
                originalWidth: img.width,
                originalHeight: img.height,
                scaledWidth: width,
                scaledHeight: height,
              },
            });
          }

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw image to canvas with scaling if needed
          ctx.drawImage(img, 0, 0, width, height);

          // Convert canvas to blob in PNG format
          canvas.toBlob(
            blob => {
              // Clean up object URL
              if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
              }

              if (!blob) {
                reject(new Error("Failed to convert image to PNG"));
                return;
              }

              // Validate blob size (Gemini max: 7MB)
              const maxSize = 7 * 1024 * 1024; // 7MB
              if (blob.size > maxSize) {
                reject(
                  new Error(
                    `Image is too large (${(blob.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 7MB.`
                  )
                );
                return;
              }

              // Create new File object with PNG data
              const pngFile = new File(
                [blob],
                file.name.replace(/\.[^.]+$/, ".png"),
                {
                  type: "image/png",
                  lastModified: Date.now(),
                }
              );

              // Validate the file is actually PNG
              if (pngFile.type !== "image/png") {
                reject(new Error("Failed to convert image to PNG format"));
                return;
              }

              logger.info("Image converted to PNG", {
                component: "ImageConverter",
                metadata: {
                  originalType: file.type,
                  originalSize: file.size,
                  convertedSize: pngFile.size,
                  originalName: file.name,
                  convertedName: pngFile.name,
                  originalDimensions: `${img.width}x${img.height}`,
                  convertedDimensions: `${width}x${height}`,
                },
              });

              resolve(pngFile);
            },
            "image/png",
            0.95
          ); // High quality PNG
        } catch (error) {
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
          }
          logger.error(
            "Error during image conversion",
            {
              component: "ImageConverter",
              metadata: { fileName: file.name, fileType: file.type },
            },
            error as Error
          );
          reject(error);
        }
      };

      img.onerror = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
        logger.error(
          "Error loading image for conversion",
          {
            component: "ImageConverter",
            metadata: { fileName: file.name, fileType: file.type },
          },
          new Error("Image failed to load")
        );
        reject(
          new Error(
            `Failed to load image for conversion. The file may be corrupted or in an unsupported format.`
          )
        );
      };

      // Load the image
      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    } catch (error) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      logger.error(
        "Error setting up image conversion",
        {
          component: "ImageConverter",
          metadata: { fileName: file.name, fileType: file.type },
        },
        error as Error
      );
      reject(error);
    }
  });
}

/**
 * Checks if a file needs to be converted to PNG
 * @param file - The file to check
 * @returns boolean - True if conversion is needed (supported format that is not PNG)
 */
export function needsPngConversion(file: File): boolean {
  // Get the effective file type (use inferred if file.type is missing)
  let effectiveType = file.type;
  if (!effectiveType || effectiveType.trim() === "") {
    effectiveType = inferImageTypeFromFileName(file.name) || "";
  }

  // If we still don't have a type, we can't determine if conversion is needed
  if (!effectiveType) {
    return false;
  }

  // Only convert supported formats that are not PNG
  const isSupported = isSupportedImageFormat(file);
  const needsConversion = isSupported && effectiveType !== "image/png";

  if (needsConversion) {
    logger.debug("Image needs PNG conversion", {
      component: "ImageConverter",
      metadata: { fileName: file.name, fileType: effectiveType },
    });
  }

  return needsConversion;
}

/**
 * Infers image MIME type from file extension
 * @param fileName - The file name to check
 * @returns The inferred MIME type or null if unknown
 */
function inferImageTypeFromFileName(
  fileName: string | undefined | null
): string | null {
  // Handle undefined, null, or empty file names
  if (!fileName || typeof fileName !== "string" || fileName.trim() === "") {
    return null;
  }

  const extension = fileName.toLowerCase().split(".").pop();
  if (!extension) {
    return null;
  }

  const extensionMap: Record<string, string> = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
    heic: "image/heic",
    heif: "image/heif",
  };
  return extensionMap[extension] || null;
}

/**
 * Validates that a file is a supported image format
 * @param file - The file to validate
 * @returns boolean - True if the format is supported
 */
export function isSupportedImageFormat(file: File): boolean {
  // If file.type is set, check it directly
  if (file.type) {
    return SUPPORTED_IMAGE_FORMATS.includes(file.type as any);
  }

  // If file.type is missing, try to infer from file name
  const inferredType = inferImageTypeFromFileName(file.name);
  if (inferredType) {
    return SUPPORTED_IMAGE_FORMATS.includes(inferredType as any);
  }

  // If we can't determine the type, it's not supported
  return false;
}

/**
 * Processes an image file, converting to PNG if necessary
 * Validates format and ensures output is PNG
 * @param file - The image file to process
 * @returns Promise<File> - The processed file (PNG format)
 */
export async function processImageFile(file: File): Promise<File> {
  try {
    // Validate file has a name
    if (!file.name || typeof file.name !== "string") {
      throw new Error(
        "Invalid file: file name is missing. Please ensure you're uploading a valid image file."
      );
    }

    // Normalize file type - use inferred type if file.type is missing
    let fileType = file.type;
    if (!fileType || fileType.trim() === "") {
      const inferredType = inferImageTypeFromFileName(file.name);
      if (inferredType) {
        fileType = inferredType;
        // Create a new File object with the inferred type for consistency
        // Note: We can't modify the original File object, but we'll use the inferred type for validation
        logger.debug("Inferred image type from file name", {
          component: "ImageConverter",
          metadata: {
            fileName: file.name,
            inferredType: fileType,
          },
        });
      } else {
        throw new Error(
          `Unable to determine image format for "${file.name || "unknown file"}". Please ensure the file has a valid extension (.png, .jpg, .jpeg, .webp, .heic, .heif) or a valid MIME type.`
        );
      }
    }

    // Validate file is a supported image format
    if (!isSupportedImageFormat(file)) {
      const formatDisplay = fileType || "unknown";
      throw new Error(
        `Unsupported image format: ${formatDisplay}. Supported formats: PNG, JPEG, JPG, WEBP, HEIC, HEIF`
      );
    }

    // Validate file size (10MB limit for upload, but will be checked again for Gemini 7MB limit)
    const maxUploadSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxUploadSize) {
      throw new Error(
        `Image is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 10MB.`
      );
    }

    // Convert to PNG if needed
    // Use the normalized file type for the check
    const normalizedFile = file.type
      ? file
      : new File([file], file.name, {
          type: fileType,
          lastModified: file.lastModified,
        });

    if (needsPngConversion(normalizedFile)) {
      logger.info("Converting image to PNG", {
        component: "ImageConverter",
        metadata: { fileName: file.name, fileType: fileType },
      });
      const convertedFile = await convertImageToPng(normalizedFile);

      // Double-check the converted file is PNG
      if (convertedFile.type !== "image/png") {
        throw new Error("Image conversion failed: output is not PNG format");
      }

      return convertedFile;
    }

    // Already PNG - validate it's actually PNG
    if (fileType !== "image/png") {
      throw new Error("File type mismatch: expected PNG format");
    }

    logger.debug("Image already in PNG format", {
      component: "ImageConverter",
      metadata: { fileName: file.name, fileType: fileType },
    });

    // If the original file didn't have a type, return a normalized version
    if (!file.type) {
      return normalizedFile;
    }

    return file;
  } catch (error) {
    logger.error(
      "Failed to process image file",
      {
        component: "ImageConverter",
        metadata: { fileName: file.name, fileType: file.type },
      },
      error as Error
    );
    throw error;
  }
}
